import React, { useEffect, useRef, useState } from "react";
import Draggable, { DraggableEventHandler } from "react-draggable";
import queryString from "query-string";

// @ts-ignore
import setQuery from "set-query-string";

import ArrowUpIcon from "icons/ArrowUpIcon";
import ArrowDownIcon from "icons/ArrowDownIcon";
import { useDisplaySettings } from "./DisplaySettingsProvider";
import classNames from "classnames";

const DisplayComponent = ({
  id,
  style,
  canAdjustHeight,
  children,
}: {
  id: string;
  style: React.CSSProperties;
  canAdjustHeight?: boolean;
  children: ({
    sizeMultiplier,
    heightMultiplier,
  }: {
    sizeMultiplier: number;
    heightMultiplier: number;
  }) => React.ReactNode;
}) => {
  const { editMode, visibility } = useDisplaySettings();
  const hasInitialized = useRef(false);
  const [state, setState] = useState({
    sizeMultiplier: 1,
    heightMultiplier: 1,
    currentPosition: { x: 0, y: 0 },
  });

  useEffect(() => {
    if (hasInitialized.current) {
      const dataToStore = {
        s: state.sizeMultiplier,
        h: state.heightMultiplier,
        p: state.currentPosition,
      };

      let prevEncodedData: string | (string | null)[] | null = "";
      if (window.location.search) {
        const queryData = queryString.parse(window.location.search);
        prevEncodedData = queryData[id];
      }
      const encodedData = btoa(JSON.stringify(dataToStore));
      if (!prevEncodedData || prevEncodedData !== encodedData) {
        setQuery({ [id]: encodedData });
      }
    }
  }, [id, state]);

  useEffect(() => {
    try {
      if (window.location.search) {
        const queryData = queryString.parse(window.location.search);
        const encodedData = queryData[id];
        if (encodedData && typeof encodedData === "string") {
          const data = JSON.parse(atob(encodedData));
          setState({
            sizeMultiplier: data.s,
            heightMultiplier: data.h,
            currentPosition: data.p,
          });
        }
      }
    } catch (e) {
      console.log("Failed to parse position data", e);
    }

    hasInitialized.current = true;
  }, [id]);

  const onDragStop: DraggableEventHandler = (event, position) => {
    setState((prev) => ({
      ...prev,
      currentPosition: { x: position.x, y: position.y },
    }));
  };

  const grow = () => {
    setState((prev) => ({
      ...prev,
      sizeMultiplier: prev.sizeMultiplier + 0.1,
    }));
  };

  const shrink = () => {
    setState((prev) => ({
      ...prev,
      sizeMultiplier: prev.sizeMultiplier - 0.1,
    }));
  };

  const increaseHeight = () => {
    setState((prev) => ({
      ...prev,
      heightMultiplier: prev.heightMultiplier + 0.1,
    }));
  };

  const decreaseHeight = () => {
    setState((prev) => ({
      ...prev,
      heightMultiplier: prev.heightMultiplier - 0.1,
    }));
  };

  const { sizeMultiplier, heightMultiplier } = state;

  if (!visibility[id]) {
    return null;
  }

  return (
    <Draggable
      position={state.currentPosition}
      onStop={onDragStop}
      disabled={!editMode}
    >
      <div
        className={classNames("display-component", editMode && "edit-mode")}
        style={
          {
            ...style,
            position: "absolute",
            minHeight: 40,
            "--size-multiplier": sizeMultiplier,
            "--height-multiplier": heightMultiplier,
          } as React.CSSProperties
        }
      >
        <div className="resizer">
          <button onClick={grow} className="resizer-button">
            +
          </button>
          <button onClick={shrink} className="resizer-button">
            -
          </button>
        </div>
        {canAdjustHeight && (
          <div className="height-sizer">
            <div className="resize-button" onClick={decreaseHeight}>
              <ArrowUpIcon />
            </div>
            <div className="resize-button" onClick={increaseHeight}>
              <ArrowDownIcon />
            </div>
          </div>
        )}
        {children({ sizeMultiplier, heightMultiplier })}
      </div>
    </Draggable>
  );
};

export default DisplayComponent;
